import { RUNTIME } from '../../config/runtime';
import type { Department, DocumentRecord, QRRecord, User } from '../../types';
import { apiRequest } from '../api';

interface CloudDocument extends DocumentRecord {
  qr?: QRRecord | null;
}

export interface CloudSnapshot {
  departments: Department[];
  documents: DocumentRecord[];
  qrRecords: QRRecord[];
  users: User[];
}

export async function fetchCloudSnapshot(includeUsers = false): Promise<CloudSnapshot> {
  if (RUNTIME.mode !== 'cloud') return { departments: [], documents: [], qrRecords: [], users: [] };
  const [departmentResponse, documentResponse] = await Promise.all([
    apiRequest<{ items: Department[] }>('/api/departments'),
    apiRequest<{ items: CloudDocument[] }>('/api/documents'),
  ]);

  const userResponse = includeUsers ? await apiRequest<{ items: User[] }>('/api/users') : { items: [] as User[] };
  const departments = departmentResponse.items.map(normalizeDepartment);
  const qrRecords = documentResponse.items.flatMap(item => item.qr ? [item.qr] : []);
  const documents = documentResponse.items.map(item => {
    const { qr: _qr, ...document } = item;
    void _qr;
    return normalizeDocument(document);
  });
  return { departments, documents, qrRecords, users: userResponse.items.map(normalizeUser) };
}

function normalizeDepartment(department: Department): Department {
  const now = new Date().toISOString();
  const theme = department.theme as Partial<Department['theme']> | undefined;
  const layout = department.layout as Partial<Department['layout']> | undefined;
  const quota = department.quota as Partial<Department['quota']> | undefined;
  return {
    ...department,
    theme: {
      primaryColor: theme?.primaryColor ?? '#B8720A',
      secondaryColor: theme?.secondaryColor ?? '#020C1B',
      accentColor: theme?.accentColor ?? '#D4A030',
      darkMode: theme?.darkMode ?? true,
      logoUrl: theme?.logoUrl,
      bannerUrl: theme?.bannerUrl,
    },
    layout: {
      mobileWidgets: layout?.mobileWidgets ?? [],
      desktopWidgets: layout?.desktopWidgets ?? [],
      draftWidgets: layout?.draftWidgets,
      lastPublished: layout?.lastPublished,
      lastDraft: layout?.lastDraft,
    },
    categories: department.categories ?? [],
    quota: {
      usedBytes: quota?.usedBytes ?? 0,
      totalBytes: quota?.totalBytes ?? 16_106_127_360,
      defaultMaxBytes: quota?.defaultMaxBytes ?? 512_000,
      exceptionMaxBytes: quota?.exceptionMaxBytes ?? 2_097_152,
      warningThreshold: quota?.warningThreshold ?? 0.8,
    },
    managerId: department.managerId ?? '',
    members: department.members ?? [],
    documentCount: department.documentCount ?? 0,
    status: department.status ?? 'active',
    createdAt: department.createdAt ?? now,
    updatedAt: department.updatedAt ?? now,
  };
}

function normalizeDocument(document: DocumentRecord): DocumentRecord {
  return {
    ...document,
    fileSizeBytes: Number(document.fileSizeBytes ?? 0),
    compressedBytes: Number(document.compressedBytes ?? 0),
    pageCount: Number(document.pageCount ?? 1),
    tags: document.tags ?? [],
    versions: document.versions ?? [],
  };
}

function normalizeUser(user: User): User {
  return { ...user, departments: user.departments ?? [], isActive: user.isActive !== false, createdAt: user.createdAt ?? new Date().toISOString() };
}
